import { useState, useEffect } from 'react';
import api from '../services/api';
import { ArrowLeft, ChevronRight, Loader2, PoundSterling, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const B29_URL = `${API}/api/b29-transfers`;
const ORANGE = '#ee7203';

const money = (v) => `£${(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * "View Price Lists" slide-out panel. Same look and behaviour as the panel on the B29 Stock Transfer modal and Customer
 * Management's Pricing panel: a list of suppliers, click one to see its products by category. For now it only holds the
 * B29 price lists; more price lists will be added here later. When the quote passes `onAddProduct`, every product also gets a
 * "+" button under its code that adds that product to the quote at its cost price.
 */
const B29PriceListPanel = ({ onAddProduct = null }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`${B29_URL}/catalog/suppliers`);
        if (alive) setSuppliers(res.data?.suppliers || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || 'Failed to load the B29 price lists');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const openSupplier = async (supplier) => {
    setSelected(supplier);
    setCatalog(null);
    setCatalogLoading(true);
    try {
      const res = await api.get(`${B29_URL}/catalog/suppliers/${supplier.id}/products`);
      setCatalog(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load this supplier\'s products');
      setSelected(null);
    } finally {
      setCatalogLoading(false);
    }
  };

  const backToSuppliers = () => { setSelected(null); setCatalog(null); };

  return (
    <div className="h-full flex flex-col" data-testid="quote-pricelist-panel">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-orange-100 flex-shrink-0">
        {selected && (
          <button onClick={backToSuppliers} className="text-gray-400 hover:text-gray-700 rounded-full p-1 hover:bg-gray-100 flex-shrink-0" title="Back to all suppliers" data-testid="quote-pricelist-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
        >
          <PoundSterling className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-extrabold text-gray-900 leading-tight truncate">{selected ? selected.name : 'B29 Price Lists'}</h3>
          <p className="text-[11px] text-gray-500 truncate">
            {selected ? 'All products by category' : `${suppliers.length} supplier${suppliers.length === 1 ? '' : 's'} — click one to see its products`}
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
        {!selected && (
          loading ? (
            <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading suppliers…</span>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-sm text-red-600">{error}</div>
          ) : suppliers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <PoundSterling className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="text-sm">No B29 price lists have been loaded yet.</p>
            </div>
          ) : (
            suppliers.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => openSupplier(s)}
                className="w-full flex items-center gap-3 rounded-xl border-2 border-orange-200 bg-white px-4 py-3 text-left hover:bg-orange-50 hover:border-orange-400 transition-colors"
                data-testid={`quote-pricelist-supplier-${s.id}`}
              >
                <span className="flex-1 min-w-0 font-bold text-sm text-gray-900 truncate">{s.name}</span>
                <span className="text-[11px] font-semibold bg-orange-100 text-orange-700 rounded-full px-2 py-0.5 flex-shrink-0">
                  {s.product_count} product{s.product_count === 1 ? '' : 's'}
                </span>
                <ChevronRight className="w-4 h-4 text-orange-400 flex-shrink-0" />
              </button>
            ))
          )
        )}

        {selected && (
          catalogLoading || !catalog ? (
            <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading products…</span>
            </div>
          ) : (catalog.categories || []).length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">No products on file for this supplier.</div>
          ) : (
            catalog.categories.map((cat) => (
              <section key={cat.id} className="rounded-xl border border-gray-200 overflow-hidden bg-white" data-testid={`quote-pricelist-category-${cat.id}`}>
                <div className="px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: ORANGE }}>
                  <span className="text-sm font-bold text-white uppercase tracking-wide truncate">{cat.name}</span>
                  <span className="text-xs text-white/80 ml-auto flex-shrink-0">{cat.products.length} product{cat.products.length === 1 ? '' : 's'}</span>
                </div>
                {cat.products.map((p) => {
                  const code = p.stock_code || p.supplier_product_code || '—';
                  const tiers = (p.tiers || []).filter((t) => t.price !== null && t.price !== undefined);
                  const detailBits = [p.dimensions, p.thickness_mm ? `${p.thickness_mm}mm` : null, p.pallet_qty ? `Pallet: ${p.pallet_qty}` : null].filter(Boolean);
                  return (
                    <div key={p.id} className="grid grid-cols-[130px_minmax(0,1fr)_140px] gap-4 items-center px-4 py-3 border-t border-gray-100 hover:bg-orange-50/40">
                      <div className="min-w-0">
                        <div className="font-mono font-bold text-sm text-gray-900 break-all">{code}</div>
                        {onAddProduct && (
                          <button
                            type="button"
                            onClick={() => onAddProduct(p)}
                            title="Add to the quote, priced at its cost price"
                            className="mt-1.5 inline-flex items-center justify-center w-7 h-7 rounded-full text-white shadow-sm hover:shadow-md active:scale-95 transition-all"
                            style={{ backgroundColor: ORANGE }}
                            data-testid={`quote-pricelist-add-${p.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="min-w-0 flex items-center gap-4 flex-wrap">
                        <p className="text-sm text-gray-800">{p.description || '—'}</p>
                        {detailBits.length > 0 && (
                          <p className="text-xs text-gray-400 whitespace-nowrap">{detailBits.join(' · ')}</p>
                        )}
                        {tiers.length > 1 && (
                          <p className="text-xs text-amber-700 whitespace-nowrap">
                            {tiers.map((t) => `${t.label}: ${money(t.price)}`).join(' · ')}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-base font-bold text-gray-900 tabular-nums">{money(p.best_price)}</div>
                        <div className="text-xs text-gray-400">per {p.price_uom || p.order_uom || 'Each'}</div>
                      </div>
                    </div>
                  );
                })}
              </section>
            ))
          )
        )}
      </div>
    </div>
  );
};

export default B29PriceListPanel;
